import { lazy, Suspense, useEffect } from "react";
import { createPortal } from "react-dom";
import { enUS } from "@embedpdf/plugin-i18n";
import Icon from "@/components/icon";
import { faIR } from "@/features/content/pdf-viewer/locale";
import { usePdfDocument } from "@/features/content/pdf-viewer/hooks/use-pdf-document";
import { useBackButton } from "@/store/use-back-button";
import { SafeArea } from "@/components/top-bar";

// Heavy WASM/canvas engine — only loaded when the reader is actually opened.
const PDFViewer = lazy(() =>
  import("@embedpdf/react-pdf-viewer").then((m) => ({ default: m.PDFViewer })),
);

type Props = {
  url: string;
  title?: string;
  onClose: () => void;
};

/** Centred Persian loading state with an optional download percentage. */
function LoadingState({ progress }: { progress: number | null }) {
  return (
    <div dir="rtl" className="flex h-full flex-col items-center justify-center gap-1 text-default-600">
      <p className="text-sm font-medium text-default-800">در حال دانلود فایل…</p>
      {progress != null ? (
        <p className="text-xs text-default-500">{progress.toLocaleString("fa-IR")} %</p>
      ) : null}
    </div>
  );
}

/** Persian error state with a retry button for when the download keeps failing. */
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center text-default-600">
      <Icon name="close-circle-fill" size={40} className="text-danger" />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-default-800">بارگذاری فایل با مشکل مواجه شد</p>
        <p className="text-xs text-default-500">
          اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white"
      >
        تلاش مجدد
      </button>
    </div>
  );
}


export default function PdfViewerScreen({ url, title, onClose }: Props) {
  useBackButton(onClose, true);

  const { status, buffer, progress, retry } = usePdfDocument(url);

  // Lock background scroll while the reader covers the screen.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Portal to <body> so the full-screen reader escapes any transformed ancestor
  // (e.g. the content-v2 playlist drawer, which applies a CSS transform that
  // would otherwise trap this `fixed inset-0` overlay inside the drawer).
  const overlay = (
    <div dir="rtl" className="fixed inset-0 z-[100] flex flex-col bg-background mx-auto max-w-xl">
      <SafeArea/>

      <div className="relative flex-1 overflow-hidden" dir="ltr">
        {status === "error" ? (
          <ErrorState onRetry={retry} />
        ) : status === "ready" && buffer ? (
          <Suspense fallback={<LoadingState progress={100} />}>
            <PDFViewer
              config={{
                documentManager: {
                  initialDocuments: [{ buffer, name: title || "document.pdf" }],
                },
                i18n: {
                  defaultLocale: "fa",
                  fallbackLocale: "en",
                  locales: [faIR, enUS],
                },
                theme: { preference: "light" },
                tabBar: "never",
                // Stop the stamp plugin from fetching @embedpdf/default-stamps
                // manifests (fa + en + stamps.pdf) from jsdelivr on every open.
                stamp: { manifests: [], defaultLibrary: false },
                disabledCategories: [
                  "annotation",
                  "redaction",
                  "history",
                  "panel",
                  "selection",
                  "document",
                  "insert",
                  "form",
                  // "page",
                  // "tools",
                ],
              }}
              style={{ width: "100%", height: "100%" }}
            />
          </Suspense>
        ) : (
          <LoadingState progress={progress} />
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(overlay, document.body)
    : overlay;
}
